import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useToast } from '@ntconsult/aluma-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { campanhasQueries, convitesQueries, painelQueries } from '../queries';
import { convitesService } from '../services/convitesService';
import type { PedidoRodada } from '../types/Convites';
import type { Idioma } from '../types/Painel';

export function usePreviaConvite(
  id: string,
  participanteId: string | undefined,
  habilitado: boolean,
  idioma?: Idioma,
) {
  return useQuery({ ...convitesQueries.previa(id, participanteId, idioma), enabled: habilitado });
}

/** Prévia do rascunho (spec 30 R11); `null` = nada a pedir (campo vazio ou chave aberta). */
export function usePreviaRascunho(
  id: string,
  idioma: Idioma,
  rascunho: { assunto: string; corpo: string } | null,
) {
  return useQuery({
    ...convitesQueries.previaRascunho(id, idioma, rascunho ?? { assunto: '', corpo: '' }),
    enabled: rascunho !== null,
  });
}

export function useConvites(id: string) {
  return useQuery(convitesQueries.resumo(id));
}

/** Inicia a rodada (202): grava a rodada devolvida no cache para o polling começar na hora. */
export function useIniciarRodada(id: string) {
  const qc = useQueryClient();
  const { t } = useTranslation(['convites', 'erros']);
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: (pedido: PedidoRodada) => convitesService.iniciar(id, pedido),
    onSuccess: (rodada) => {
      qc.setQueryData(convitesQueries.resumo(id).queryKey, (atual) =>
        atual ? { ...atual, ultimaRodada: rodada } : atual,
      );
      // A ação principal some durante a rodada (spec 28 R16, linha 1).
      void qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey });
    },
  });

  /**
   * `null` se a rodada começou; senão o código do erro, que o modal de envio trata (spec 30 R17).
   * Só os ignorados viram toast: o andamento aparece no painel (R19).
   */
  async function iniciar(pedido: PedidoRodada) {
    try {
      const rodada = await mutation.mutateAsync(pedido);
      if (rodada.ignorados)
        toast.success({ title: t('convites:iniciadaIgnorados', { count: rodada.ignorados }) });
      return null;
    } catch (error) {
      // 409 pode indicar cache defasado (rodada já em andamento / campanha mudou).
      void qc.invalidateQueries({ queryKey: convitesQueries.resumo(id).queryKey });
      void qc.invalidateQueries({ queryKey: painelQueries.all(id) });
      return mensagemDeErro(error);
    }
  }

  return { iniciar, isPending: mutation.isPending };
}

export function useModelosConvite(id: string) {
  return useQuery(convitesQueries.modelos(id));
}

/** Salvar (`modelo`) ou restaurar o padrão (`null`) de um idioma (spec 25 R8). */
export function useSalvarModelo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { idioma: Idioma; modelo: { assunto: string; corpo: string } | null }) =>
      v.modelo
        ? convitesService.salvarModelo(id, v.idioma, v.modelo)
        : convitesService.restaurarModelo(id, v.idioma),
    // Devolve a promessa: o `mutateAsync` só resolve com o gravado novo no cache (spec 30 R15).
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: convitesQueries.modelos(id).queryKey }),
        // A prévia depende do modelo.
        qc.invalidateQueries({ queryKey: [...convitesQueries.resumo(id).queryKey, 'previa'] }),
      ]),
  });
}
